import isFlagOn from '../isFlagOn';

// Instellingen per deellink. Het onderzoek bepaalt wat er standaard aanstaat;
// een link kan daar alleen iets van UITzetten, nooit iets aan. Zo blijft de
// AI-interviewer achter de schakelaar op het onderzoek (die toetst de server in
// `functions/aiInterviewer/gate.js`), ook als iemand de URL met de hand aanpast.
//
// Een link wordt nergens opgeslagen — hij ís de URL — dus de keuze reist mee als
// `?uit=ProgressBar,ReadAloud`.

/**
 * @typedef {object} LinkSetting
 * @property {'AIInterviewer'|'ProgressBar'|'ReadAloud'|'VisualAnalogScale'} key - Veldnaam op het Research-document
 * @property {string} label - Dezelfde naam als in de onderzoeksbouwer
 */

/** @type {LinkSetting[]} */
const LINK_SETTINGS = [
    { key: 'AIInterviewer', label: 'AI Interviewer' },
    // Zet ook de mini animatie aan: die is onderdeel van de balk, geen eigen
    // instelling meer. Het oude veld `MiniGame` wordt nergens meer gelezen.
    { key: 'ProgressBar', label: 'Geanimeerde voortgangsbalk' },
    { key: 'ReadAloud', label: 'Vragen voorlezen' },
    // AI-CONTEXT: See docs/features/vas-slider.md
    { key: 'VisualAnalogScale', label: 'Schuifbalk in plaats van rondjes' },
];

const PARAM = 'uit';
const KNOWN_KEYS = LINK_SETTINGS.map((s) => s.key);

/**
 * @param {string[]} [keys]
 * @returns {string[]} Alleen bekende sleutels, in vaste volgorde
 */
const onlyKnown = (keys) => KNOWN_KEYS.filter((key) => (keys || []).includes(key));

/**
 * @param {string[]} [offKeys] - Instellingen die deze link uitzet
 * @returns {string} `''` of `?uit=…`, klaar om achter een pad te plakken
 */
const buildLinkSettingsQuery = (offKeys) => {
    const keys = onlyKnown(offKeys);
    return keys.length ? `?${PARAM}=${keys.join(',')}` : '';
};

/**
 * @param {string} search - `window.location.search` of `useLocation().search`
 * @returns {string[]} De instellingen die de link uitzet
 */
const parseLinkOffKeys = (search) => {
    const raw = new URLSearchParams(search || '').get(PARAM) || '';
    return onlyKnown(raw.split(','));
};

/**
 * Het onderzoek zoals deze link het ziet.
 *
 * @template {object|undefined|null} T
 * @param {T} research - Het Research-document
 * @param {string} search - De query string van de invul-URL
 * @returns {T} Hetzelfde document als de link niets uitzet
 */
const applyLinkSettings = (research, search) => {
    const offKeys = parseLinkOffKeys(search);
    if (!research || offKeys.length === 0) return research;
    const overrides = Object.fromEntries(offKeys.map((key) => [key, 'false']));
    return { ...research, ...overrides };
};

/**
 * De mini animatie (onderdeel van de geanimeerde voortgangsbalk) hoort bij het
 * zelf invullen van een vraag; in een gesprek met Decca speelt hij ertussendoor.
 * De balk zelf mag wél naast de interviewer. Bewust geen melding op het scherm:
 * de schakelaar heet 'Geanimeerde voortgangsbalk', en die werkt gewoon.
 *
 * @param {object|undefined|null} research - Het onderzoek, na `applyLinkSettings`
 * @returns {boolean} true als de mini animatie niet mag spelen
 */
const miniGameBlocked = (research) => isFlagOn(research?.AIInterviewer);

export {
    LINK_SETTINGS,
    buildLinkSettingsQuery,
    parseLinkOffKeys,
    applyLinkSettings,
    miniGameBlocked,
};
